import type { CSSProperties, RefObject } from 'react';
import { PULL_THRESHOLD_PX, usePullToRefresh } from '@/hooks/usePullToRefresh';
import { Icon } from './Icon';
import './pull-indicator.css';

/**
 * Pull-to-refresh, drawn (plan 15 §5.4, chunk H): the gesture of
 * `usePullToRefresh` and the disc that answers it, in one element a route
 * mounts next to its list.
 *
 * The disc comes down under the app bar with the finger and its arrow turns
 * with the pull; past the threshold it fills with the accent (release now
 * refreshes); while the refresh runs it spins, and it goes back up when the
 * refresh settles. It is its own component so that following a finger
 * re-renders this, not the list.
 *
 * The disc is decoration, hidden from assistive technology: the gesture is
 * touch-only and every screen that has it also has a Refresh control. The
 * status line says "Refreshing…" for a screen-reader user who pulled anyway.
 * Under `prefers-reduced-motion` nothing spins or slides (pull-indicator.css);
 * the disc simply appears and goes.
 */

export interface PullIndicatorProps {
  /** The list the pull starts on: touches outside it are not pulls. */
  targetRef: RefObject<HTMLElement | null>;
  /** The list's refresh; the disc spins until the promise it returns settles. */
  onRefresh: () => unknown;
  /** Handheld, with the list on screen. */
  enabled: boolean;
}

export function PullIndicator({ targetRef, onRefresh, enabled }: PullIndicatorProps) {
  const { phase, distance } = usePullToRefresh(targetRef, onRefresh, { enabled });
  if (!enabled) return null;
  const style = {
    '--pull-distance': `${distance}px`,
    '--pull-progress': Math.min(distance / PULL_THRESHOLD_PX, 1),
  } as CSSProperties;
  return (
    <>
      <div className="pull-indicator" data-phase={phase} style={style} aria-hidden="true">
        <span className="pull-indicator__disc">
          <Icon name="refresh" size={20} />
        </span>
      </div>
      <span role="status" className="visually-hidden">
        {phase === 'refreshing' ? 'Refreshing…' : ''}
      </span>
    </>
  );
}
